import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { eaAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ListChecks, Loader2, RefreshCw, CheckCircle2, ArrowRight, Sparkles, Clock,
  FileText, DollarSign, HardHat, CalendarPlus, BellRing, MailX, ScanLine, PartyPopper,
  CalendarClock, XCircle, GitPullRequestArrow, Mailbox,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const TYPE_META = {
  po_review: { icon: FileText, tint: 'text-indigo-300 bg-indigo-500/15 border-indigo-500/30' },
  thread_update: { icon: Mailbox, tint: 'text-violet-300 bg-violet-500/15 border-violet-500/30' },
  overcharge: { icon: DollarSign, tint: 'text-red-400 bg-red-500/15 border-red-500/30' },
  no_trade_tomorrow: { icon: HardHat, tint: 'text-orange-300 bg-orange-500/15 border-orange-500/30' },
  no_site_date: { icon: CalendarPlus, tint: 'text-amber-300 bg-amber-400/15 border-amber-400/30' },
  reminder_due: { icon: BellRing, tint: 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30' },
  delivery_failed: { icon: MailX, tint: 'text-rose-300 bg-rose-500/15 border-rose-500/30' },
  invoice_check: { icon: ScanLine, tint: 'text-slate-300 bg-slate-500/15 border-slate-500/30' },
  payment_reconcile: { icon: DollarSign, tint: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30' },
};

const UPDATE_ICON = {
  date_change: CalendarClock,
  cancellation: XCircle,
  variation: GitPullRequestArrow,
  general_update: Mailbox,
};

const PRIORITY_META = {
  high: { label: 'Needs you now', dot: 'bg-red-500' },
  medium: { label: 'Worth doing today', dot: 'bg-amber-400' },
  low: { label: 'When you get a sec', dot: 'bg-cyan-400' },
};

const ActionListPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [snoozing, setSnoozing] = useState(null);
  const [applying, setApplying] = useState(null);
  const [dismissing, setDismissing] = useState(null);

  const load = async (isRefresh) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await eaAPI.actionList();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load your action list');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dropItem = (item) => setData((d) => ({
    ...d,
    items: d.items.filter((i) => i.id !== item.id),
    counts: { ...d.counts, total: d.counts.total - 1, [item.priority]: Math.max(0, d.counts[item.priority] - 1) },
  }));

  const handleResolve = async (item) => {
    setResolving(item.id);
    try {
      await eaAPI.resolveInvoice(item.entity_id);
      toast.success('Marked as reviewed');
      dropItem(item);
    } catch (e) {
      toast.error('Could not update');
    } finally {
      setResolving(null);
    }
  };

  const handleSnooze = async (item) => {
    setSnoozing(item.id);
    try {
      await eaAPI.snooze(item.id);
      toast.success('Snoozed until tomorrow');
      dropItem(item);
    } catch (e) {
      toast.error('Could not snooze');
    } finally {
      setSnoozing(null);
    }
  };

  const applyLabel = (item) => {
    if (item.update_type === 'date_change') return item.proposed_on_site_date ? `Reschedule to ${item.proposed_on_site_date}` : 'Apply date';
    if (item.update_type === 'cancellation') return 'Cancel job';
    return 'Acknowledge';
  };

  const handleApply = async (item) => {
    setApplying(item.id);
    try {
      const res = await eaAPI.applyThreadUpdate(item.entity_id);
      toast.success(res.data?.change || 'Applied');
      dropItem(item);
    } catch (e) {
      toast.error('Could not apply this update');
    } finally {
      setApplying(null);
    }
  };

  const handleDismiss = async (item) => {
    setDismissing(item.id);
    try {
      await eaAPI.dismissThreadUpdate(item.entity_id);
      toast.success('Dismissed');
      dropItem(item);
    } catch (e) {
      toast.error('Could not dismiss');
    } finally {
      setDismissing(null);
    }
  };

  const handleApplyPayment = async (item) => {
    setApplying(item.id);
    try {
      const res = await eaAPI.applyPaymentMatch(item.entity_id);
      const n = res.data?.updated?.length || 0;
      toast.success(n ? `Marked ${n} invoice(s) as paid` : 'Payment reconciled');
      dropItem(item);
    } catch (e) {
      toast.error('Could not reconcile this payment');
    } finally {
      setApplying(null);
    }
  };

  const handleDismissPayment = async (item) => {
    setDismissing(item.id);
    try {
      await eaAPI.dismissPaymentMatch(item.entity_id);
      toast.success('Dismissed');
      dropItem(item);
    } catch (e) {
      toast.error('Could not dismiss');
    } finally {
      setDismissing(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const items = data?.items || [];
  const grouped = ['high', 'medium', 'low'].map((p) => ({ p, list: items.filter((i) => i.priority === p) }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="action-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl brand-gradient flex items-center justify-center shadow-glow flex-shrink-0">
            <ListChecks className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              Action List
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 flex items-center gap-1">
                <Sparkles className="h-2.5 w-2.5" /> AI
              </span>
            </h1>
            <p className="text-sm text-muted-foreground">
              {data?.counts?.total > 0
                ? `${data.counts.total} thing${data.counts.total === 1 ? '' : 's'} need you — everything else is handled.`
                : 'Your assistant is on top of everything.'}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => load(true)} disabled={refreshing} data-testid="refresh-actions-btn">
          {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Summary chips */}
      {data?.counts?.total > 0 && (
        <div className="flex flex-wrap gap-2">
          {['high', 'medium', 'low'].filter((p) => data.counts[p] > 0).map((p) => (
            <span key={p} className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full surface">
              <span className={`h-2 w-2 rounded-full ${PRIORITY_META[p].dot}`} />
              {data.counts[p]} {PRIORITY_META[p].label.toLowerCase()}
            </span>
          ))}
        </div>
      )}

      {/* Empty state */}
      {items.length === 0 && (
        <Card className="surface" data-testid="action-empty">
          <CardContent className="py-16 text-center">
            <PartyPopper className="h-14 w-14 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold">You're all caught up</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">
              No POs to review, no overcharges, no gaps in tomorrow's schedule. Your AI assistant will surface anything that needs you here.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Grouped items */}
      {grouped.map(({ p, list }) => list.length > 0 && (
        <div key={p} className="space-y-3">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${PRIORITY_META[p].dot}`} />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{PRIORITY_META[p].label}</h2>
            <span className="text-xs text-muted-foreground">({list.length})</span>
          </div>
          <div className="space-y-2.5">
            {list.map((item) => {
              const meta = TYPE_META[item.type] || TYPE_META.po_review;
              const Icon = (item.type === 'thread_update' && UPDATE_ICON[item.update_type]) || meta.icon;
              return (
                <Card key={item.id} className="surface hover:border-primary/30 transition-colors" data-testid={`action-item-${item.type}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`h-10 w-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${meta.tint}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium leading-tight truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.thread_update ? (
                        <>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDismiss(item)}
                            disabled={dismissing === item.id}
                            data-testid={`dismiss-thread-${item.entity_id}`}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {dismissing === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Dismiss'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApply(item)}
                            disabled={applying === item.id}
                            data-testid={`apply-thread-${item.entity_id}`}
                          >
                            {applying === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> {applyLabel(item)}</>}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => navigate(item.link)} data-testid={`goto-${item.id}`} title="Open job">
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </>
                      ) : item.reconcile ? (
                        <>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDismissPayment(item)}
                            disabled={dismissing === item.id}
                            data-testid={`dismiss-payment-${item.entity_id}`}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {dismissing === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Dismiss'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApplyPayment(item)}
                            disabled={applying === item.id}
                            data-testid={`apply-payment-${item.entity_id}`}
                          >
                            {applying === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm paid</>}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => navigate(item.link)} data-testid={`goto-${item.id}`} title="Open invoices">
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {item.resolvable && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleResolve(item)}
                              disabled={resolving === item.id}
                              data-testid={`resolve-${item.id}`}
                              className="text-muted-foreground hover:text-emerald-400"
                            >
                              {resolving === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Done</>}
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleSnooze(item)}
                            disabled={snoozing === item.id}
                            data-testid={`snooze-${item.id}`}
                            className="text-muted-foreground hover:text-foreground"
                            title="Snooze until tomorrow"
                          >
                            {snoozing === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                          </Button>
                          <Button size="sm" onClick={() => navigate(item.link)} data-testid={`goto-${item.id}`}>
                            {item.cta || 'Open'} <ArrowRight className="h-4 w-4 ml-1.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {data?.generated_at && (
        <p className="text-center text-xs text-muted-foreground pt-2">
          Updated {format(new Date(data.generated_at), 'h:mma, d MMM')}
        </p>
      )}
    </div>
  );
};

export default ActionListPage;
